/** 添付ダウンロードリンクの画面向け純粋関数(設計 §10.4・§14)。node 試験 tests/mail-files.test.mjs から import する。 */
import type { MailDetail, MailDownloadRecord, MailDownloadSummary, MailFile, MailLink, MailLinkStatus, MailMessage, MailRevokeManyResult, MailRevokeResult } from "./mailTypes";

export const LINK_STATES: Record<MailLinkStatus, string> = { prepared: "送出前", active: "有効", expired: "期限切れ", revoked: "失効" };
export const ATTACHMENT_MODES: Record<string, string> = { default: "既定", keep: "添付のまま", link: "ダウンロードリンクにする" };
/** §5.4 のリンク化できない理由。未知のコードはそのまま出す。 */
export const LINK_UNAVAILABLE_REASONS: Record<string, string> = {
  smime: "S/MIME のメール", mime: "MIME 構造を書き換えられない", from_invalid: "From を解釈できない", header_duplicate: "ヘッダーが重複している",
  dkim_domain: "DKIM を付けられないドメイン", too_large: "書き換え後のメールが受信上限を超える", config_changed: "設定が途中で変わった", spool_full: "添付の実体を保存できない(スプール上限など)", license: "ライセンスの状態",
};
export const REVOKE_RESULTS: Record<MailRevokeResult | "request_failed", string> = {
  revoked: "失効しました", not_sent: "送出前のため失効できません(承認前は取消を使ってください)", state_changed: "既に期限切れか失効済みです",
  forbidden: "操作できる立場ではありません", not_found: "見つかりません", request_failed: "要求が失敗しました。再取得して状態を確認してください",
};
/** revoke-many は 1 要求 200 件まで(§10.3)。超える束ねは分けて送る。 */
export const REVOKE_MANY_LIMIT = 200;
export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}
const REVOKE_REASONS: Record<string, string> = { manual: "手動", expired: "期限", anonymized: "匿名化", config: "設定変更", restore: "復元" };

export function linkUnavailableText(reason: string | null): string { return reason ? LINK_UNAVAILABLE_REASONS[reason] ?? reason : ""; }
export function revokeReasonText(reason: string | null): string { return reason ? REVOKE_REASONS[reason] ?? reason : "—"; }
export function revokedByText(by: MailLink["revoked_by"]): string {
  if (!by) return "—";
  return typeof by === "string" ? (by === "sender" ? "送信者本人" : "承認者") : by.name;
}

/** 束ね(同じ Message-ID の uid 群)のリンク状態を数える。リンクが無い行(keep)は none。 */
export function summarizeLinks(rows: Pick<MailMessage, "link">[]): Record<MailLinkStatus | "none", number> {
  const out = { prepared: 0, active: 0, expired: 0, revoked: 0, none: 0 };
  for (const row of rows) out[row.link?.status ?? "none"] += 1;
  return out;
}
export function linkSummaryText(rows: Pick<MailMessage, "link">[]): string {
  const n = summarizeLinks(rows);
  if (n.prepared + n.active + n.expired + n.revoked === 0) return "";
  return [n.active ? `有効 ${n.active}` : "", n.revoked ? `失効 ${n.revoked}` : "", n.expired ? `期限切れ ${n.expired}` : "", n.prepared ? `送出前 ${n.prepared}` : ""].filter(Boolean).join(" / ");
}
/** 束ねの一括失効の対象行。有効と送出前(送出済みなら失効できる)だけを渡し、期限切れ・失効済みは除く。確認画面で uid・宛先ドメイン・状態を見せる。 */
export function revocableRows<T extends Pick<MailMessage, "uid" | "link">>(rows: T[]): T[] {
  return rows.filter((r) => r.link && (r.link.status === "active" || r.link.status === "prepared"));
}
export function revocableUids(rows: Pick<MailMessage, "uid" | "link">[]): string[] { return revocableRows(rows).map((r) => r.uid); }

/** 詳細の「リンクを失効」。出すかはサーバーの `can.revoke`(approver か送信者本人。送信者本人が割当承認者を兼ねて viewer が assigned_approver でも true)で決める。prepared かつ held は非活性にして理由を返す。 */
export function revokeState(mail: Pick<MailDetail, "can" | "status" | "link">): { show: boolean; disabledReason: string | null } {
  if (!mail.link || !mail.can.revoke) return { show: false, disabledReason: null };
  if (mail.link.status === "expired" || mail.link.status === "revoked") return { show: false, disabledReason: null };
  if (mail.link.status === "prepared" && mail.status === "held") return { show: true, disabledReason: "送出前は失効できません(承認前は取消を使ってください)" };
  return { show: true, disabledReason: null };
}
/** 再送はリンクの期限切れ・失効では不可。理由文を返し、null なら再送できる。 */
export function retryBlockedReason(mail: Pick<MailDetail, "link">): string | null {
  if (mail.link?.status === "expired") return "リンクの期限切れのため再送できません。Gmail から送り直してください";
  if (mail.link?.status === "revoked") return "リンクの失効のため再送できません。Gmail から送り直してください";
  return null;
}
/** 承認できる人への注記(§10.4)。保留中のリンク化予定と、できない理由。承認者のほか、本人確認で自分が承認する送信者(can.approve)にも出す。 */
export function approverNote(mail: Pick<MailDetail, "viewer" | "status" | "attachment_mode" | "link_unavailable_reason"> & { can?: { approve?: boolean } }): string | null {
  if (mail.status !== "held") return null;
  if (mail.viewer !== "approver" && mail.viewer !== "assigned_approver" && !mail.can?.approve) return null;
  if (mail.attachment_mode === "link") return "承認するとリンク化して送出されます";
  if (mail.link_unavailable_reason) return `リンク化できません(${linkUnavailableText(mail.link_unavailable_reason)})。添付のまま送出されます`;
  return null;
}
/** 添付行(attachments[].index)と files 行の対応付け。サーバーが scan 時の part_index で突き合わせた `attachment_index` だけを使い、名前やサイズで推測しない。
 * null の file(この機能より前のメール、multipart / .eml を丸ごと外したもの)と、添付の一覧に無い index は対応付けない。 */
export function matchFiles(attachments: MailDetail["attachments"], files: MailFile[]): Map<number, MailFile> {
  const known = new Set(attachments.map((a) => a.index));
  const out = new Map<number, MailFile>();
  for (const f of files) if (f.attachment_index !== null && known.has(f.attachment_index) && !out.has(f.attachment_index)) out.set(f.attachment_index, f);
  return out;
}
export function revokeManyText(results: MailRevokeManyResult[]): { uid: string; text: string; ok: boolean }[] {
  return results.map((r) => ({ uid: r.uid, text: (REVOKE_RESULTS as Record<string, string>)[r.result] ?? r.result, ok: r.result === "revoked" }));
}

/** 設定保存の 400(`error.message` が理由コード、`reasons` / `count` が extra)を日本語にする。該当しなければ null。 */
const PREREQ: Record<string, string> = {
  host_missing: "公開口のホストが未設定です", host_mismatch: "公開口のホストが api 側の環境変数と一致しません",
  smtp_not_configured: "確認コード送付用の SMTP が未設定です", smtp_not_secure: "SMTP が TLS ではありません", smtp_from_not_own_domain: "SMTP の From が自社ドメインではありません",
};
export function filesSettingsError(error: unknown): string | null {
  // ApiError を duck-type で見る(node 試験から拡張子なしの import を解決できないため lib/api に依存しない)。
  if (!(error instanceof Error) || (error as { status?: unknown }).status !== 400) return null;
  const key = error.message;
  const extra = (error as { extra?: Record<string, unknown> }).extra ?? {};
  if (key === "files_prerequisites") {
    const reasons = Array.isArray(extra.reasons) ? (extra.reasons as unknown[]).map(String) : [];
    const texts = reasons.map((r) => r.startsWith("dkim_not_verified:") ? `DKIM の DNS が ${r.slice("dkim_not_verified:".length)} で確認できていません` : PREREQ[r] ?? r);
    return `リンク化を使うには前提が足りません: ${texts.join("。")}`;
  }
  if (key === "links_active") return `有効なリンクが ${typeof extra.count === "number" ? extra.count : "?"} 本あるため変更できません`;
  if (key === "invalid_mail_files_host") return "公開口のホストは FQDN(例: files.example.co.jp)で入力してください";
  if (key === "invalid_mail_files_display_name") return "運営者表示は 60 文字以内にしてください";
  if (key === "link_days_exceeds_raw_retention") return "リンクの有効日数は本文の保持(日)以下にしてください";
  return null;
}

/** 記録一覧(`GET /api/dlp/mail/file-downloads`、§10.2)。viewer には集計行(count あり)、approver と送信者本人には個別記録が返る。 */
export function isDownloadSummary(row: MailDownloadRecord | MailDownloadSummary): row is MailDownloadSummary { return "count" in row; }
/** 記録一覧の問い合わせ。期間は JST の日付で受け、開始はその日の 0 時、終了はその日の末尾まで含める(サーバーは `at <= until`)。
 * scope は approver では送らない(サーバーが見ない)。空の条件は載せない。 */
export function downloadsParams(f: { uid?: string; recipientDomain?: string; since?: string; until?: string; scope?: "mine" | "all" | ""; limit: number; offset: number }): URLSearchParams {
  const p = new URLSearchParams({ limit: String(f.limit), offset: String(f.offset) });
  if (f.uid?.trim()) p.set("uid", f.uid.trim());
  if (f.recipientDomain?.trim()) p.set("recipient_domain", f.recipientDomain.trim().toLowerCase());
  if (f.since) p.set("since", `${f.since}T00:00:00+09:00`);
  if (f.until) p.set("until", `${f.until}T23:59:59.999999+09:00`);
  if (f.scope) p.set("scope", f.scope);
  return p;
}
/** 記録一覧の 403(`error.message` が理由)を日本語にする。該当しなければ null(mailError に任せる)。 */
export function downloadsErrorText(error: unknown): string | null {
  if (!(error instanceof Error) || (error as { status?: unknown }).status !== 403) return null;
  if (error.message === "employee_link_unavailable") return "本人に対応付いた記録はありません(検証済みメールと在籍中の従業員との対応付けが必要です)";
  if (error.message === "not_authorized") return "全体の記録を見るには dlp:viewer 以上のロールが必要です";
  if (error.message === "mail_feature_unavailable") return "メール DLP がいまの契約に含まれていません";
  return null;
}
